/**
 * Tiny synthesized sound effects (Web Audio, no asset files, no dependency).
 * Muting is a per-device preference kept in localStorage. The audio context
 * is created on the first sound, which always follows a tap, so browsers
 * allow it.
 */

export type Sfx = 'coin' | 'buy' | 'unlock' | 'pop' | 'chime' | 'fold';

const KEY = 'voidminer.muted';
let ctx: AudioContext | null = null;
let muted = (() => { try { return localStorage.getItem(KEY) === '1'; } catch { return false; } })();

export function isMuted(): boolean {
  return muted;
}

export function toggleMute(): boolean {
  muted = !muted;
  try { localStorage.setItem(KEY, muted ? '1' : '0'); } catch { /* private mode: session only */ }
  return muted;
}

/** One enveloped oscillator note. */
function note(freq: number, start: number, length: number, type: OscillatorType, gain: number): void {
  const c = ctx!;
  const osc = c.createOscillator();
  const amp = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, c.currentTime + start);
  amp.gain.setValueAtTime(0, c.currentTime + start);
  amp.gain.linearRampToValueAtTime(gain, c.currentTime + start + 0.01);
  amp.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + start + length);
  osc.connect(amp).connect(c.destination);
  osc.start(c.currentTime + start);
  osc.stop(c.currentTime + start + length + 0.05);
}

const PATTERNS: Readonly<Record<Sfx, readonly [number, number, number, OscillatorType, number][]>> = {
  // [frequency, start, length, wave, gain]
  coin: [[1320, 0, 0.08, 'square', 0.04], [1760, 0.06, 0.12, 'square', 0.04]],
  buy: [[520, 0, 0.09, 'triangle', 0.08], [780, 0.07, 0.12, 'triangle', 0.08]],
  unlock: [[523, 0, 0.2, 'sine', 0.08], [659, 0.08, 0.2, 'sine', 0.08], [784, 0.16, 0.3, 'sine', 0.08]],
  pop: [[220, 0, 0.06, 'sawtooth', 0.06], [880, 0.03, 0.15, 'triangle', 0.06]],
  chime: [[988, 0, 0.35, 'sine', 0.07], [1319, 0.12, 0.45, 'sine', 0.06]],
  fold: [[196, 0, 0.6, 'sine', 0.09], [294, 0.15, 0.6, 'sine', 0.08], [392, 0.3, 0.8, 'sine', 0.08]],
};

export function play(sfx: Sfx): void {
  if (muted) return;
  try {
    ctx ??= new AudioContext();
    if (ctx.state === 'suspended') void ctx.resume();
    for (const [f, start, length, type, gain] of PATTERNS[sfx]) note(f, start, length, type, gain);
  } catch {
    // No audio available (old browser, blocked context): stay silent.
  }
}
